import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import { Button, Tag, Tooltip, Upload, message as antMessage } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faTrash,
	faPaperclip,
	faShieldCheck,
	faShieldExclamation,
	faSpinner,
	faTriangleExclamation,
	faFilePdf,
	faFileWord,
	faFileLines
} from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';

import {
	LIST_PROJECT_ATTACHMENTS,
	REQUEST_ATTACHMENT_UPLOAD,
	DELETE_ATTACHMENT,
	SUBSCRIBE_TO_ATTACHMENT_STATUS
} from 'graph/documents';

import './AttachmentsSection.sass';

const ALLOWED_TYPES: Record<string, string> = {
	'application/pdf': 'PDF',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
	'text/plain': 'TXT'
};

const ICON_BY_TYPE: Record<string, any> = {
	'application/pdf': faFilePdf,
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': faFileWord,
	'text/plain': faFileLines
};

const MAX_FILE_SIZE = 50 * 1024 * 1024;

interface AttachmentItem {
	id: string;
	projectId?: string | null;
	tempSessionId?: string | null;
	filename: string;
	size: number;
	contentType: string;
	status: 'pending' | 'scanning' | 'ready' | 'infected' | 'failed';
	uploadedAt: string;
	scanError?: string | null;
}

interface RequestUploadResult {
	attachmentId: string;
	s3Key: string;
	uploadUrl: string;
	fields: Record<string, string>;
	expiresIn: number;
}

const formatSize = (bytes: number) => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const statusConfig = (t: any) => ({
	pending: { color: 'default', label: t('messages.attachments.status.pending'), icon: faSpinner, spin: true },
	scanning: { color: 'processing', label: t('messages.attachments.status.scanning'), icon: faSpinner, spin: true },
	ready: { color: 'success', label: t('messages.attachments.status.ready'), icon: faShieldCheck, spin: false },
	infected: { color: 'error', label: t('messages.attachments.status.infected'), icon: faShieldExclamation, spin: false },
	failed: { color: 'warning', label: t('messages.attachments.status.failed'), icon: faTriangleExclamation, spin: false }
});

interface AttachmentsSectionProps {
	projectId?: string;
	tempSessionId?: string;
	disabled?: boolean;
}

export const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({ projectId, tempSessionId, disabled }) => {
	const { t } = useTranslation();
	const [uploading, setUploading] = useState<Array<{ key: string; name: string; size: number; contentType: string; percent: number; error?: string }>>([]);
	const enabled = !!(projectId || tempSessionId);

	const { data, loading, refetch } = useQuery<{ listProjectAttachments: AttachmentItem[] }>(LIST_PROJECT_ATTACHMENTS, {
		variables: { projectId, tempSessionId },
		skip: !enabled,
		fetchPolicy: 'cache-and-network'
	});

	const [requestUpload] = useMutation(REQUEST_ATTACHMENT_UPLOAD);
	const [deleteAttachment, { loading: deleting }] = useMutation(DELETE_ATTACHMENT);

	useSubscription(SUBSCRIBE_TO_ATTACHMENT_STATUS, {
		variables: { projectId, tempSessionId },
		skip: !enabled,
		onData: () => { refetch(); }
	});

	const items = data?.listProjectAttachments || [];
	const cfg = useMemo(() => statusConfig(t), [t]);

	const beforeUpload = (file: File) => {
		if (!ALLOWED_TYPES[file.type]) {
			antMessage.error(t('messages.attachments.error.unsupported'));
			return Upload.LIST_IGNORE;
		}
		if (file.size > MAX_FILE_SIZE) {
			antMessage.error(t('messages.attachments.error.too_large'));
			return Upload.LIST_IGNORE;
		}
		return true;
	};

	const customUpload = async ({ file, onProgress, onSuccess, onError }: any) => {
		const f: File = file;
		const tempKey = `${f.name}-${f.size}-${Date.now()}`;
		// Optimistic local row — instantly visible regardless of GraphQL/S3 timing
		setUploading((u) => [...u, { key: tempKey, name: f.name, size: f.size, contentType: f.type, percent: 0 }]);
		try {
			const res = await requestUpload({
				variables: {
					projectId,
					tempSessionId,
					filename: f.name,
					size: f.size,
					contentType: f.type
				}
			});
			const ticketJson = res.data?.requestAttachmentUpload;
			if (!ticketJson) throw new Error('no upload ticket returned');
			const ticket: RequestUploadResult = typeof ticketJson === 'string' ? JSON.parse(ticketJson) : ticketJson;

			// Show the pending row immediately — the DDB record is created.
			refetch();

			const formData = new FormData();
			Object.entries(ticket.fields || {}).forEach(([k, v]) => formData.append(k, v as string));
			formData.append('file', f);

			const xhr = new XMLHttpRequest();
			xhr.upload.onprogress = (ev) => {
				if (ev.lengthComputable) {
					const pct = Math.round((ev.loaded / ev.total) * 100);
					setUploading((u) => u.map((x) => x.key === tempKey ? { ...x, percent: pct } : x));
					onProgress?.({ percent: pct });
				}
			};
			await new Promise<void>((resolve, reject) => {
				xhr.onload = () => {
					if (xhr.status >= 200 && xhr.status < 300) {
						resolve();
					} else {
						console.error('[attachments] S3 returned', xhr.status, xhr.statusText, xhr.responseText);
						reject(new Error(`HTTP ${xhr.status} ${xhr.statusText || ''} ${xhr.responseText?.slice(0, 200) || ''}`.trim()));
					}
				};
				xhr.onerror = () => {
					console.error('[attachments] xhr error', { url: ticket.uploadUrl, status: xhr.status, statusText: xhr.statusText });
					reject(new Error(`network error to ${new URL(ticket.uploadUrl).host} (likely CORS or DNS)`));
				};
				xhr.open('POST', ticket.uploadUrl);
				xhr.send(formData);
			});
			onSuccess?.({}, f);
			refetch();
		} catch (e: any) {
			console.error('[attachments] upload failed', e);
			antMessage.error(t('messages.attachments.error.upload_failed') + ': ' + (e?.message || ''));
			setUploading((u) => u.map((x) => x.key === tempKey ? { ...x, error: e?.message || 'failed' } : x));
			onError?.(e);
			return;
		} finally {
			// Keep the optimistic row visible while it transitions to the real DDB row.
			// We remove it after a short window so the GraphQL row replaces it
			// without flicker.
			setTimeout(() => {
				setUploading((u) => u.filter((x) => x.key !== tempKey));
			}, 1500);
		}
	};

	const onDelete = async (id: string) => {
		try {
			await deleteAttachment({ variables: { attachmentId: id } });
			refetch();
		} catch (e: any) {
			antMessage.error(t('messages.attachments.error.delete_failed') + ': ' + (e?.message || ''));
		}
	};

	const totalReady = items.filter((i) => i.status === 'ready').length;
	const totalScanning = items.filter((i) => i.status === 'scanning' || i.status === 'pending').length;

	return (
		<div className="AttachmentsSection">
			<div className="attachments-section-header">
				<span className="ai-label-with-chip">
					<span className="attachments-title-text">{t('messages.attachments.title')}</span>
					<Tooltip color="rgba(183, 235, 143, 0.92)" overlayInnerStyle={{ color: '#1f4d0a' }} overlayClassName="ai-zone-tooltip private" title={t('messages.ai_project_generation.disclosure_private_sub')}>
						<span className="ai-zone-label private xs">
							<FontAwesomeIcon icon={faShieldCheck as any} className="ai-zone-icon" />
							{t('messages.attachments.privacy_chip')}
						</span>
					</Tooltip>
				</span>
				{items.length > 0 && (
					<div className="attachments-section-meta">
						{totalReady > 0 && <Tag color="success" bordered={false}>{totalReady} {t('messages.attachments.ready_short')}</Tag>}
						{totalScanning > 0 && <Tag color="processing" bordered={false}>{totalScanning} {t('messages.attachments.scanning_short')}</Tag>}
					</div>
				)}
			</div>

			<div className="attachments-section-sub">
				{t('messages.attachments.subtitle')}
			</div>

			<Upload.Dragger
				multiple
				accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
				beforeUpload={beforeUpload}
				customRequest={customUpload}
				showUploadList={false}
				disabled={disabled}
				className="attachments-dropzone"
			>
				<div className="dropzone-inline">
					<FontAwesomeIcon icon={faPaperclip as any} />
					<div className="dropzone-text">
						<span className="dropzone-primary">{t('messages.attachments.drop_hint')}</span>
						<span className="dropzone-secondary">{t('messages.attachments.formats_hint')}</span>
					</div>
				</div>
			</Upload.Dragger>

			<div className="attachments-list">
				{loading && items.length === 0 && uploading.length === 0 && (
					<div className="attachments-empty">
						<FontAwesomeIcon icon={faSpinner} spin /> {t('common.loading')}
					</div>
				)}
				{uploading.filter((up) => !items.some((it) => it.filename === up.name && it.size === up.size)).map((up) => {
					const fileIcon = ICON_BY_TYPE[up.contentType] || faFileLines;
					return (
						<div key={up.key} className={`attachment-row status-${up.error ? 'failed' : 'pending'}`}>
							<div className="row-icon">
								<FontAwesomeIcon icon={fileIcon as any} />
							</div>
							<div className="row-main">
								<div className="row-title">
									<span className="row-filename">{up.name}</span>
									<Tag color={up.error ? 'error' : 'processing'} bordered={false} icon={<FontAwesomeIcon icon={(up.error ? faTriangleExclamation : faSpinner) as any} spin={!up.error} style={{ marginRight: 4 }} />}>
										{up.error ? up.error : `${t('messages.attachments.status.pending')} · ${up.percent}%`}
									</Tag>
								</div>
								<div className="row-meta">
									{ALLOWED_TYPES[up.contentType] || up.contentType} · {formatSize(up.size)}
								</div>
							</div>
						</div>
					);
				})}
				{items.map((item) => {
					const sc = cfg[item.status] || cfg.pending;
					const fileIcon = ICON_BY_TYPE[item.contentType] || faFileLines;
					return (
						<div key={item.id} className={`attachment-row status-${item.status}`}>
							<div className="row-icon">
								<FontAwesomeIcon icon={fileIcon as any} />
							</div>
							<div className="row-main">
								<div className="row-title">
									<span className="row-filename">{item.filename}</span>
									<Tag color={sc.color} bordered={false} icon={<FontAwesomeIcon icon={sc.icon as any} spin={sc.spin} style={{ marginRight: 4 }} />}>
										{sc.label}
									</Tag>
									{item.scanError && (
										<Tooltip title={item.scanError}>
											<Tag color="error" bordered={false}>{item.scanError}</Tag>
										</Tooltip>
									)}
								</div>
								<div className="row-meta">
									{ALLOWED_TYPES[item.contentType] || item.contentType} · {formatSize(item.size)}
								</div>
							</div>
							<div className="row-actions">
								<Tooltip title={t('messages.attachments.delete')}>
									<Button
										type="text"
										danger
										size="small"
										loading={deleting}
										icon={<FontAwesomeIcon icon={faTrash} />}
										onClick={() => onDelete(item.id)}
									/>
								</Tooltip>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};
